import {createReduxStore, register, useDispatch, useSelect} from '@wordpress/data';
import {apiFetch as API_FETCH, controls as dataControls, select as SELECT} from '@wordpress/data-controls';
import qs from 'qs';
import {getAppliedFilters} from '../components/catalog/Filters';
import {CATALOG_STORE_KEY} from "../constants";

const IN_FLIGHT = new Set();

const STORAGE_KEY = 'epi_catalog_state';

const loadState = () => {
    try {
        if (typeof window === 'undefined') return {};
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
        return {};
    }
};

const saveState = state => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
    }
};

const isOnCatalogPage = () => {
    const p = window.location.pathname.replace(/\/$/, '');
    return p === '/shop' || p.startsWith('/product-category/');
};

const buildCatalogUrl = (filterValues, search, sort, filterConfigs) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (sort) params.set('sort', sort);

    Object.entries(filterValues).forEach(([id, val]) => {
        if (val == null) return;
        if (id === 'search' || id === 'sort') return;
        const config = filterConfigs.find(c => c.id === id);

        if (config?.type === 'range' && Array.isArray(val)) {
            const [min, max] = val;
            if (typeof min === 'number') params.set(`${id}_min`, String(min));
            if (typeof max === 'number') params.set(`${id}_max`, String(max));
        } else if (Array.isArray(val)) {
            if (val.length > 0) params.set(id, val.join(','));
        } else if (typeof val === 'object') {
            // ignored
        } else if (val !== '') {
            params.set(id, String(val));
        }
    });

    const normalize = p => p.replace(/\/$/, '') || '/';
    const current = normalize(window.location.pathname);
    const isOnCategoryPage = current.startsWith('/product-category/');

    let pathname;
    if (filterValues?.categories?.slug) {
        pathname = `/product-category/${filterValues.categories.slug}`;
    } else if (!isOnCatalogPage() || (isOnCategoryPage && !filterValues?.categories)) {
        pathname = '/shop';
    } else {
        pathname = current;
    }

    const queryString = params.toString();
    return `${pathname}${queryString ? `?${queryString}` : ''}`;
};

const findInTree = (nodes, predicate) => {
    for (const node of (nodes || [])) {
        if (predicate(node)) return node;
        const found = findInTree(node.subcategories, predicate);
        if (found) return found;
    }
    return null;
};

const persisted = loadState();

const epiCatalog = typeof window !== 'undefined' ? window.epiCatalog : null;
const isProductCategoryPage = !!epiCatalog?.termId && epiCatalog?.taxonomy === 'product_cat';

const initialFilterValues = isProductCategoryPage
    ? (() => {
        const v = {...(persisted.filterValues ?? {})};
        delete v.categories;
        return v;
    })()
    : (persisted.filterValues ?? {});

const DEFAULT_STATE = {
    page: persisted.page ?? 0,
    size: persisted.size ?? 10,
    search: persisted.search ?? '',
    stagedSearch: persisted.search ?? '',
    sort: persisted.sort ?? '',
    source: 'BEST_BUY',
    _initialized: false,

    filterConfigs: [
        {
            id: 'categories',
            type: 'category',
            field: 'categoryId',
            label: 'Category',
            hidden: false,
            chipHidden: true
        },
        {
            id: 'price',
            type: 'range',
            field: 'salePrice',
            label: 'Price',
            min: 300,
            max: 5000,
            dual: true,
            prefix: '$',
            anyLabel: 'Any price'
        },
        {
            id: 'offers', type: 'multi-checkbox', field: 'offers', label: 'Special Offers',
            options: [
                {
                    label: 'On Sale',
                    value: 'on_sale',
                    filter: {field: 'percentSavings', operator: 'GREATER_THAN', value: 0}
                },
                {
                    label: 'Free Shipping',
                    value: 'free_shipping',
                    filter: {field: 'freeShipping', operator: 'EQUALS', value: 'true'}
                },
                {label: 'Deal of the Day', value: 'deal_of_the_day'},
                {label: 'Special Offer', value: 'special_offer'},
            ]
        },
        {
            id: 'source', type: 'multi-checkbox', field: 'SOURCE', label: 'Source', disabled: true, hidden: true,
            options: [
                {label: 'Best Buy', value: 'BEST_BUY'},
            ]
        },
        {id: 'rating', type: 'rating', field: 'customerReviewAverage', label: 'Minimum Rating'},
        {
            id: 'manufacturer',
            type: 'search',
            field: 'manufacturer',
            label: 'Manufacturer',
            attribute: 'manufacturer',
            multiple: true
        },
        {id: 'color', type: 'search', field: 'color', label: 'Color', attribute: 'color', multiple: true},
    ],
    filterValues: initialFilterValues,
    stagedFilterValues: initialFilterValues,

    data: null,
    loading: false,
    error: null,

    _prevRequest: null,
};

let fetchProductsTimeout = null;

const actions = {
    setPage: page => ({type: 'SET_PAGE', page}),
    * setSize(size) {
        yield {type: 'SET_SIZE', size};
        yield {type: 'SET_PAGE', page: 0};
    },
    setSearch: search => ({type: 'SET_SEARCH', search}),
    stageSearch: search => ({type: 'STAGE_SEARCH', search}),
    setSource: source => ({type: 'SET_SOURCE', source}),
    setSort: sort => ({type: 'SET_SORT', sort}),

    setFilterValue: (id, value) => ({type: 'SET_FILTER_VALUE', id, value}),
    setFilterValues: values => ({type: 'SET_FILTER_VALUES', values}),
    stageFilterValue: (id, value) => ({type: 'STAGE_FILTER_VALUE', id, value}),
    commitStagedFilters: () => ({type: 'COMMIT_STAGED_FILTERS'}),
    syncStagedFilters: () => ({type: 'SYNC_STAGED_FILTERS'}),
    removeFilter: filterId => ({type: 'REMOVE_FILTER', filterId}),
    removeFilterValue: (filterId, value) => ({type: 'REMOVE_FILTER_VALUE', filterId, value}),
    resetFilters: () => ({type: 'RESET_FILTERS'}),
    resetStagedFilters: () => ({type: 'RESET_STAGED_FILTERS'}),

    * fetchProducts({delayMs = 300} = {}) {
        if (fetchProductsTimeout) {
            clearTimeout(fetchProductsTimeout);
            fetchProductsTimeout = null;
        }
        fetchProductsTimeout = setTimeout(() => {
            wp.data.dispatch(CATALOG_STORE_KEY).__runFetchProducts();
        }, delayMs);
    },

    * __runFetchProducts() {
        const snap = yield SELECT(CATALOG_STORE_KEY, 'getRequestSnapshot');
        const prev = yield SELECT(CATALOG_STORE_KEY, 'getPrevRequestSnapshot');

        if (prev && JSON.stringify(prev) === JSON.stringify(snap)) return;

        const key = JSON.stringify(snap);
        if (IN_FLIGHT.has(key)) return;
        IN_FLIGHT.add(key);

        yield {type: 'SET_PREV_REQUEST', payload: snap};
        yield {type: 'PRODUCTS_LOADING'};

        const {query, filters} = snap;

        try {
            const payload = yield API_FETCH({
                path: `/api/products?${qs.stringify({...query, source: undefined})}`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': epiData.nonce,
                },
                body: JSON.stringify([...filters]),
            });
            yield {type: 'PRODUCTS_RECEIVE', payload};
        } catch (err) {
            yield {type: 'PRODUCTS_FAILED', err};
        } finally {
            IN_FLIGHT.delete(key);
        }
    },

    * init() {
        booted = false;
        yield {type: 'SET_INITIALIZED', value: false};

        const categoryWooId = window.epiCatalog?.taxonomy === 'product_cat' ? window.epiCatalog?.termId : null;
        const params = new URLSearchParams(window.location.search);
        const values = {};

        const rangeKeys = new Set();
        params.forEach((_, key) => {
            if (key.endsWith('_min') || key.endsWith('_max')) {
                rangeKeys.add(key.replace(/_min$|_max$/, ''));
            }
        });

        rangeKeys.forEach(id => {
            const min = params.get(`${id}_min`);
            const max = params.get(`${id}_max`);
            values[id] = [
                min != null ? Number(min) : undefined,
                max != null ? Number(max) : undefined,
            ];
            params.delete(`${id}_min`);
            params.delete(`${id}_max`);
        });

        params.forEach((raw, key) => {
            if (key === 'categoryId' || key === 'sort' || key === 'search') return;
            let v = raw;

            if (raw === 'true' || raw === 'false') {
                v = raw === 'true';
            } else if (/^\d+(\.\d+)?$/.test(raw)) {
                v = Number(raw);
            } else if (raw.includes(',')) {
                v = raw.split(',').map(s => s.trim()).filter(Boolean);
            } else if (key === 'source' || key === 'categories' || key === 'offers' || key === 'manufacturer' || key === 'color') {
                v = [raw];
            }

            values[key] = v;
        });

        const sort = params.get('sort');
        const search = params.get('search') ?? '';
        const categoryId = params.get('categoryId');

        const hasValues = Object.keys(values).length > 0;
        if (hasValues || categoryId || categoryWooId) {
            yield actions.setFilterValues(values);
            yield actions.setSort(sort);
        }
        yield actions.setSearch(search);

        // resolve category from tree instead of API calls
        const tree = wp.data.select('epi/categories').getTree();

        if (categoryWooId) {
            const cat = findInTree(tree, n => n.woocommerceId === Number(categoryWooId));
            if (cat) yield actions.setFilterValue('categories', cat);
        } else if (categoryId) {
            const cat = findInTree(tree, n => n.id === categoryId);
            if (cat) yield actions.setFilterValue('categories', cat);
        } else if (!hasValues) {
            yield actions.setFilterValue('categories', null);
        }

        yield actions.setPage(0);
        yield actions.fetchProducts();
        yield {type: 'SET_INITIALIZED', value: true};
    },

    * applyFilters() {
        if (!isOnCatalogPage()) {
            const stagedFilterValues = yield SELECT(CATALOG_STORE_KEY, 'getStagedFilterValues');
            const stagedSearch = yield SELECT(CATALOG_STORE_KEY, 'getStagedSearch');
            const sort = yield SELECT(CATALOG_STORE_KEY, 'getSort');
            const filterConfigs = yield SELECT(CATALOG_STORE_KEY, 'getFilterConfigs');
            const targetUrl = buildCatalogUrl(stagedFilterValues, stagedSearch, sort, filterConfigs);
            window.location.assign(targetUrl);
            return;
        }
        yield {type: 'COMMIT_STAGED_FILTERS'};
    },
};

function reducer(state = DEFAULT_STATE, action) {
    switch (action.type) {
        case 'SET_PAGE':
            return {...state, page: action.page};
        case 'SET_SIZE':
            return {...state, size: action.size};
        case 'SET_SEARCH':
            return {...state, search: action.search, stagedSearch: action.search};
        case 'STAGE_SEARCH':
            return {...state, stagedSearch: action.search};
        case 'SET_SOURCE':
            return {...state, source: action.source};
        case 'SET_SORT':
            return {...state, sort: action.sort};
        case 'SET_INITIALIZED':
            return {...state, _initialized: action.value};

        case 'SET_FILTER_VALUE': {
            const nextValues = {...state.filterValues, [action.id]: action.value};
            saveState({...state, filterValues: nextValues});
            return {...state, filterValues: nextValues, stagedFilterValues: nextValues};
        }
        case 'SET_FILTER_VALUES': {
            saveState({...state, filterValues: action.values});
            return {...state, filterValues: action.values, stagedFilterValues: action.values};
        }
        case 'STAGE_FILTER_VALUE':
            return {...state, stagedFilterValues: {...state.stagedFilterValues, [action.id]: action.value}};
        case 'COMMIT_STAGED_FILTERS': {
            const nextValues = {...state.stagedFilterValues};
            const nextSearch = state.stagedSearch;
            saveState({...state, filterValues: nextValues, search: nextSearch});
            return {...state, filterValues: nextValues, search: nextSearch};
        }
        case 'SYNC_STAGED_FILTERS':
            return {...state, stagedFilterValues: state.filterValues, stagedSearch: state.search};
        case 'RESET_STAGED_FILTERS':
            return {...state, stagedFilterValues: {}, stagedSearch: ''};
        case 'REMOVE_FILTER': {
            const next = {...state.filterValues};
            delete next[action.filterId];
            saveState({...state, filterValues: next});
            return {...state, filterValues: next, stagedFilterValues: next};
        }
        case 'REMOVE_FILTER_VALUE': {
            const current = state.filterValues[action.filterId];
            let nextValues;
            if (Array.isArray(current)) {
                const next = current.filter(v => v !== action.value);
                if (next.length === 0) {
                    nextValues = {...state.filterValues};
                    delete nextValues[action.filterId];
                } else {
                    nextValues = {...state.filterValues, [action.filterId]: next};
                }
            } else {
                nextValues = {...state.filterValues};
                delete nextValues[action.filterId];
            }
            saveState({...state, filterValues: nextValues});
            return {...state, filterValues: nextValues, stagedFilterValues: nextValues};
        }
        case 'RESET_FILTERS': {
            saveState({...state, filterValues: {}, search: ''});
            return {...state, filterValues: {}, search: '', stagedFilterValues: {}, stagedSearch: ''};
        }

        case 'PRODUCTS_LOADING':
            return {...state, loading: true, error: null};
        case 'PRODUCTS_RECEIVE':
            return {...state, loading: false, data: action.payload};
        case 'PRODUCTS_FAILED':
            return {...state, loading: false, error: action.err};

        case 'SET_PREV_REQUEST':
            return {...state, _prevRequest: action.payload};
        default:
            return state;
    }
}

const selectors = {
    getPage: state => state.page,
    getSize: state => state.size,
    getSearch: state => state.search,
    getStagedSearch: state => state.stagedSearch,
    getSource: state => state.source,
    getSort: state => state.sort,
    isInitialized: state => state._initialized,

    getFilterConfigs: state => state.filterConfigs,
    getFilterValues: state => state.filterValues,
    getStagedFilterValues: state => state.stagedFilterValues,

    getAppliedFilters: state => getAppliedFilters(state.filterConfigs, state.filterValues),

    getData: state => state.data,
    isLoading: state => state.loading,
    getError: state => state.error,

    getSelectedCategory: state => state.filterValues.categories,
    getStagedSelectedCategory: state => state.stagedFilterValues.categories,

    getRequestSnapshot: state => {
        const query = {page: state.page, size: state.size, search: state.search, source: state.source};
        if (state.sort) query.sort = state.sort;
        const filters = getAppliedFilters(state.filterConfigs, state.filterValues);
        return {query, filters};
    },
    getPrevRequestSnapshot: state => state._prevRequest,

    getPersistable: state => ({
        filterValues: state.filterValues,
        page: state.page,
        size: state.size,
        search: state.search,
        stagedSearch: state.stagedSearch,
        sort: state.sort,
    }),

    getViewModel: state => ({
        page: state.page,
        size: state.size,
        search: state.search,
        stagedSearch: state.stagedSearch,
        sort: state.sort,
        source: state.source,
        filterConfigs: state.filterConfigs,
        filterValues: state.filterValues,
        stagedFilterValues: state.stagedFilterValues,
        data: state.data,
        loading: state.loading,
        error: state.error,
        selectedCategory: state.filterValues.categories,
        stagedSelectedCategory: state.stagedFilterValues.categories,
    }),
};

export const useCatalogDispatch = () => useDispatch(CATALOG_STORE_KEY);

export const useCatalogSelector = () => useSelect(
    select => select(CATALOG_STORE_KEY)?.getViewModel() || DEFAULT_STATE,
    []
);

const controls = {
    ...dataControls,
    DELAY({ms}) {
        return new Promise(r => setTimeout(r, ms));
    },
};

const store = createReduxStore(CATALOG_STORE_KEY, {
    reducer,
    actions,
    selectors,
    controls,
});

register(store);

wp.data.subscribe(() => {
    const persist = wp.data.select(CATALOG_STORE_KEY)?.getPersistable?.();
    if (!persist) return;
    saveState(persist);
});

let lastSig = null;
let booted = false;

const sigOf = s => JSON.stringify(s);

wp.data.subscribe(() => {
    const sel = wp.data.select(CATALOG_STORE_KEY);
    if (!sel || !sel.getRequestSnapshot) return;

    const snap = sel.getRequestSnapshot();
    const sig = sigOf(snap);

    if (booted) {
        const prev = sel.getPrevRequestSnapshot();
        if (prev) {
            const changedSearch = prev.query.search !== snap.query.search;
            const changedFilters = sigOf(prev.filters) !== sigOf(snap.filters);
            const changedSort = prev.query.sort !== snap.query.sort;
            if (changedSearch || changedFilters || changedSort) {
                if (sel.getPage() !== 0) wp.data.dispatch(CATALOG_STORE_KEY).setPage(0);
            }
        }
    }

    if (sig !== lastSig) {
        lastSig = sig;
        if (booted) {
            wp.data.dispatch(CATALOG_STORE_KEY).fetchProducts();
        }
    }

    if (!booted) {
        booted = true;
    }
});

let lastUrlSig = window.location.pathname.replace(/\/$/, '') + window.location.search;

wp.data.subscribe(() => {
    const sel = wp.data.select(CATALOG_STORE_KEY);
    if (!sel || !booted || !isOnCatalogPage()) return;
    if (!sel.isInitialized()) return;

    const filterValues = sel.getFilterValues();
    const search = sel.getSearch();
    const sort = sel.getSort();
    const filterConfigs = sel.getFilterConfigs();
    if (!filterValues || !filterConfigs) return;

    const targetUrl = buildCatalogUrl(filterValues, search, sort, filterConfigs);
    if (targetUrl === lastUrlSig) return;
    lastUrlSig = targetUrl;
    history.replaceState(null, '', targetUrl);
});

export const useCatalog = () => {
    const dispatch = useCatalogDispatch();
    const selector = useCatalogSelector();
    return {...dispatch, ...selector};
};